import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { storeItem } from '#/shared/utils/storage';
import {
  positionFetching,
  positionError,
  watchCleared,
  watchStarted,
  watchPaused,
  watchResumed,
  geolocationOutOfBounds as geolocationOutOfBoundsAction,
} from '#/shared/slices/location';
import { positionSuccess } from '#/shared/queries/location';
import { GEOLOCATION_APPROVED_STORAGE_KEY } from '#/shared/constants';

const POSITION_DEBOUNCE_TIME = 500;

export default () => {
  const dispatch = useDispatch();
  const currentPosition = useSelector(s => s.shared.location.currentPosition);
  const geolocationApproved = useSelector(
    s => s.shared.location.geolocationApproved
  );

  const memoizedGeolocationSuccess = useCallback(
    (p, successCallback, includeLabel = false) => {
      dispatch(positionSuccess(p, includeLabel ? successCallback : null));
      storeItem(GEOLOCATION_APPROVED_STORAGE_KEY, true);
      if (successCallback && !includeLabel) successCallback(p);
    },
    [dispatch]
  );
  const geolocationSuccessDebounced = useDebouncedCallback(
    memoizedGeolocationSuccess,
    POSITION_DEBOUNCE_TIME
  );

  const memoizedGelocationError = useCallback(
    (error, errorCallback) => {
      dispatch(
        // `error` is a GeolocationPositionError object and not serializable
        // so only send the properties we're interested in to the reducer
        positionError({
          message: error.message,
          code: error.code,
        })
      );
      storeItem(GEOLOCATION_APPROVED_STORAGE_KEY, false);
      if (errorCallback) errorCallback(error);
    },
    [dispatch]
  );
  const geolocationErrorDebounced = useDebouncedCallback(
    memoizedGelocationError,
    POSITION_DEBOUNCE_TIME
  );

  const getCurrentPositionAsync = useCallback(
    async (includeLabel = false) =>
      new Promise(resolve => {
        dispatch(positionFetching());
        navigator.geolocation.getCurrentPosition(
          p => geolocationSuccessDebounced(p, resolve, includeLabel),
          error => geolocationErrorDebounced(error, () => resolve(null))
        );
      }),
    [geolocationSuccessDebounced, geolocationErrorDebounced, dispatch]
  );

  const startPositionWatch = useCallback(
    (attemptingGeoRefresh, successCallback, errorCallback) => {
      dispatch(positionFetching({ attemptingGeoRefresh }));
      const id = navigator.geolocation.watchPosition(
        p => {
          geolocationSuccessDebounced(p, successCallback);
        },
        error => {
          geolocationErrorDebounced(error, errorCallback);
        }
      );
      dispatch(watchStarted({ watchId: id }));
    },
    [dispatch, geolocationErrorDebounced, geolocationSuccessDebounced]
  );

  const clearPositionWatch = useCallback(
    id => {
      navigator.geolocation.clearWatch(id);
      dispatch(watchCleared());
    },
    [dispatch]
  );

  const geolocationOutOfBounds = useCallback(() => {
    dispatch(geolocationOutOfBoundsAction());
    storeItem(GEOLOCATION_APPROVED_STORAGE_KEY, false);
  }, [dispatch]);

  const geolocationPaused = useCallback(() => {
    dispatch(watchPaused());
  }, [dispatch]);

  const geolocationResumed = useCallback(() => {
    dispatch(watchResumed());
  }, [dispatch]);

  return {
    currentPosition,
    getCurrentPositionAsync,
    startPositionWatch,
    clearPositionWatch,
    geolocationApproved,
    geolocationOutOfBounds,
    geolocationPaused,
    geolocationResumed,
  };
};
